import { IdType, type ExamineeValidationError } from '@exam-center/graphql';
import { type ApolloError } from '@shared/apollo';

import { type ImportExamineeErrorCardProps } from '../../components/import-examinee-error-card';

const IMPORT_WR_EXAMINEE_ERROR_CODE = {
  CANNOT_CONNECT_TO_WR: 'CANNOT_CONNECT_TO_WR',
  NO_EXAMINEE_FOUND: 'NO_EXAMINEE_FOUND',
  EXAM_EVENT_CLOSE_REGISTRATION: 'EXAM_EVENT_CLOSE_REGISTRATION',
  RECEIVED_EXAMINEES_EXCEEDS_EXAM_EVENT_SEAT: 'RECEIVED_EXAMINEES_EXCEEDS_EXAM_EVENT_SEAT',
  DUPLICATE_EXAMINEE_ID_NUMBER: 'DUPLICATE_EXAMINEE_ID_NUMBER',
} as const;

export type ImportFromWrErrorCode =
  (typeof IMPORT_WR_EXAMINEE_ERROR_CODE)[keyof typeof IMPORT_WR_EXAMINEE_ERROR_CODE];

export function getImportFromWRErrorCode(error: ApolloError) {
  if (!error.graphQLErrors?.length || !error.graphQLErrors[0]?.extensions?.code) {
    return undefined;
  }

  const extensions = error.graphQLErrors[0]?.extensions as
    | { code?: string; response?: { code?: string } }
    | undefined;

  const errorCode =
    extensions?.code === 'INTERNAL_SERVER_ERROR' ? extensions?.response?.code : extensions?.code;

  if (
    !errorCode ||
    !Object.values(IMPORT_WR_EXAMINEE_ERROR_CODE).includes(errorCode as ImportFromWrErrorCode)
  ) {
    return undefined;
  }

  return IMPORT_WR_EXAMINEE_ERROR_CODE[errorCode as keyof typeof IMPORT_WR_EXAMINEE_ERROR_CODE];
}

export function getExamineeCountAndSeatFromError(error: ApolloError) {
  const errorCode = getImportFromWRErrorCode(error);

  if (errorCode === 'RECEIVED_EXAMINEES_EXCEEDS_EXAM_EVENT_SEAT') {
    const details = error.graphQLErrors[0]?.extensions?.details as
      | {
          value?: {
            receivedExaminees?: number;
            examEventSeat?: number;
          };
        }
      | undefined;

    return {
      wrExamineeCount: details?.value?.receivedExaminees ?? 0,
      examEventSeat: details?.value?.examEventSeat ?? 0,
    };
  }

  return null;
}

export function getDuplicateIdNumbersFromError(error: ApolloError) {
  const errorCode = getImportFromWRErrorCode(error);

  if (errorCode === 'DUPLICATE_EXAMINEE_ID_NUMBER') {
    const details = error.graphQLErrors[0]?.extensions?.details as
      | {
          value?: string[];
        }
      | undefined;

    return details?.value ?? [];
  }

  return [];
}

// Subset of the backend's `ImportExamEventExamineesErrorCode` enum that
// `ecSyncExamEventExamineeWithWr` can actually emit for a WR sync.
type WrSyncErrorCode =
  | 'MISSING_REQUIRED_FIELD'
  | 'EXAM_CODE_NOT_FOUND'
  | 'DUPLICATED_ID_NUMBER'
  | 'INVALID_EMAIL_PATTERN'
  | 'INVALID_ID_CARD_NUMBER_PATTERN'
  | 'INVALID_PASSPORT_NUMBER_PATTERN'
  | 'INVALID_PHONE_NUMBER_PATTERN'
  | 'EXAMINEE_IN_BLOCKLIST'
  | 'INVALID_NAME_PATTERN'
  | 'UPDATE_UNABLE_ON_ALLOCATED_EXAM_COURSE';

const WR_SYNC_ERROR_MESSAGES: Record<WrSyncErrorCode, string> = {
  MISSING_REQUIRED_FIELD: 'กรอกข้อมูลไม่ครบถ้วน',
  EXAM_CODE_NOT_FOUND: 'รหัสหลักสูตร (Exam Code) ไม่ถูกต้องหรือไม่มีในระบบ',
  DUPLICATED_ID_NUMBER: 'พบเลขที่บัตรประชาชนหรือเลขที่พาสปอร์ตซ้ำ',
  INVALID_EMAIL_PATTERN: 'รูปแบบอีเมลไม่ถูกต้อง เช่น ไม่มี @ หรือ .com',
  INVALID_ID_CARD_NUMBER_PATTERN:
    'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น',
  INVALID_PASSPORT_NUMBER_PATTERN:
    'รูปแบบเลขพาสปอร์ตไม่ถูกต้อง กรุณาระบุเป็นตัวอักษร A-Z หรือเลข 0-9\nและต้องมีความยาว 6-9 ตัวอักษร',
  INVALID_PHONE_NUMBER_PATTERN:
    'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง ต้องเป็นตัวเลข 10 หลักขึ้นต้นด้วย 0\nหรือขึ้นต้นด้วยรหัสประเทศ (+66) ตามด้วยตัวเลข 9 หลัก',
  EXAMINEE_IN_BLOCKLIST: 'รายชื่อนี้ถูกระงับสิทธิ์การสอบ (Blocklist)',
  INVALID_NAME_PATTERN:
    "รูปแบบชื่อ-นามสกุลไม่ถูกต้อง ต้องมีตัวอักษรภาษาไทย ภาษาอังกฤษ หรืออักษรพิเศษที่กำหนด (.'-) เท่านั้น",
  UPDATE_UNABLE_ON_ALLOCATED_EXAM_COURSE:
    'ไม่สามารถแก้ไขหลักสูตรได้ เนื่องจากมีการจัดสรรชุดข้อสอบให้ผู้เข้าสอบนี้แล้ว',
};

// Thai labels for the `property` a MISSING_REQUIRED_FIELD error points at, so the
// card can say which field is missing instead of just "กรอกข้อมูลไม่ครบถ้วน".
const MISSING_FIELD_LABELS: Record<string, string> = {
  firstName: 'ชื่อ',
  lastName: 'นามสกุล',
  email: 'อีเมล',
  phoneNumber: 'เบอร์โทรศัพท์',
  title: 'คำนำหน้า',
};

function getMissingFieldLabel(
  property: string | null | undefined,
  idType: IdType | null | undefined,
): string {
  if (!property) return '';

  if (property === 'idNumber') {
    return idType === IdType.Passport ? 'พาสปอร์ต' : 'เลขบัตรประชาชน';
  }
  return MISSING_FIELD_LABELS[property] ?? property;
}

// Builds the card title, e.g. "สมชาย ใจดี | เลขบัตร: 1-2345-..." (or just the name
// when the examinee has no id number).
function getExamineeErrorCardTitle(
  name: string,
  idType: IdType | null | undefined,
  idNumber: string | null | undefined,
): string {
  const title = [];

  if (name) title.push(name);

  if (idType && idNumber) {
    const idLabel = idType === IdType.Passport ? 'พาสปอร์ต' : 'เลขบัตร';
    title.push(`${idLabel}: ${idNumber}`);
  }

  return title.join(' | ');
}

export type ExamineeValidationErrorItem = Pick<
  ExamineeValidationError,
  'id' | 'firstName' | 'middleName' | 'lastName' | 'idNumber' | 'idType' | 'code' | 'property'
>;

/**
 * Groups per-property WR sync errors by examinee id into one error card per examinee,
 * since `ecSyncExamEventExamineeWithWr` returns one entry per invalid field, not per examinee.
 * All MISSING_REQUIRED_FIELD errors for the same examinee are further combined into a
 * single "ไม่พบ" message listing every missing field, instead of one bullet per field.
 */
export function groupExamineeValidationErrorsIntoImportExamineeErrorCards(
  errors: ExamineeValidationErrorItem[],
): ImportExamineeErrorCardProps[] {
  const identitiesById = new Map<string, Omit<ImportExamineeErrorCardProps, 'errors'>>();
  const missingFieldLabelsById = new Map<string, string[]>();
  const otherMessagesById = new Map<string, string[]>();

  for (const { id, firstName, middleName, lastName, idNumber, idType, code, property } of errors) {
    if (!identitiesById.has(id)) {
      const name = [firstName, middleName, lastName].filter(Boolean).join(' ');
      identitiesById.set(id, {
        title: getExamineeErrorCardTitle(name, idType, idNumber),
      });
    }

    if (code === 'MISSING_REQUIRED_FIELD') {
      const label = getMissingFieldLabel(property, idType);
      const labels = missingFieldLabelsById.get(id) ?? [];
      if (!labels.includes(label)) labels.push(label);
      missingFieldLabelsById.set(id, labels);
      continue;
    }

    const message = WR_SYNC_ERROR_MESSAGES[code as WrSyncErrorCode] ?? code;
    const messages = otherMessagesById.get(id) ?? [];
    if (!messages.includes(message)) messages.push(message);
    otherMessagesById.set(id, messages);
  }

  return Array.from(identitiesById.entries()).map(([id, identity]) => {
    const missingLabels = missingFieldLabelsById.get(id);
    const otherMessages = otherMessagesById.get(id) ?? [];
    const errors = missingLabels?.length
      ? [
          `${WR_SYNC_ERROR_MESSAGES.MISSING_REQUIRED_FIELD} — ไม่พบ ${missingLabels.join(' / ')}`,
          ...otherMessages,
        ]
      : otherMessages;

    return { ...identity, errors };
  });
}

const WR_RESULT_MODAL_TITLES = {
  SUCCESS: 'นำเข้ารายชื่อผู้มีสิทธิ์สอบสำเร็จ',
  PARTIAL_SUCCESS: 'นำเข้ารายชื่อสำเร็จบางส่วน',
  FAILURE: 'นำเข้ารายชื่อผู้มีสิทธิ์สอบไม่สำเร็จ',
};

const WR_RESULT_MODAL_DESCRIPTIONS = {
  SUCCESS: 'การนำเข้าข้อมูลจาก WR เสร็จสิ้น',
  PARTIAL_SUCCESS:
    'การนำเข้าข้อมูลจาก WR เสร็จสิ้น พบรายชื่อบางส่วนที่นำเข้าหรือแก้ไขข้อมูลไม่สำเร็จ\nกรุณาตรวจสอบรายละเอียดด้านล่าง',
  FAILURE: 'ไม่มีรายชื่อใดนำเข้าสำเร็จ กรุณาตรวจสอบรายละเอียดด้านล่าง',
};

export function getWRResultModalTitleAndDescription(hasErrors: boolean, hasResult: boolean) {
  if (!hasErrors)
    return {
      title: WR_RESULT_MODAL_TITLES.SUCCESS,
      description: WR_RESULT_MODAL_DESCRIPTIONS.SUCCESS,
    };
  if (hasResult)
    return {
      title: WR_RESULT_MODAL_TITLES.PARTIAL_SUCCESS,
      description: WR_RESULT_MODAL_DESCRIPTIONS.PARTIAL_SUCCESS,
    };
  return {
    title: WR_RESULT_MODAL_TITLES.FAILURE,
    description: WR_RESULT_MODAL_DESCRIPTIONS.FAILURE,
  };
}
